import { useEffect, useId, useMemo, useRef, useState } from 'react'

/**
 * Replaces the select2 dropdowns the pages initialised with
 * `theme: 'bootstrap-5'`.
 *
 * select2 needed jQuery and had to be destroyed and re-created every time its
 * options changed — which is exactly what home.js, vacancy.js and the profile
 * forms all did by hand. This keeps what users actually got from it (a
 * placeholder, type-to-filter, keyboard control) using Bootstrap's own classes,
 * so the theme CSS applies without the plugin.
 *
 * The empty string is the "All" / unselected value, matching the
 * `<option value=''>All</option>` every one of those dropdowns started with.
 */
export interface SelectOption {
  value: string
  label: string
}

/**
 * How many rows are painted at once. Roughly eight fit the 320px menu, so this
 * is a dozen screens of scrolling before the footer asks the user to type.
 */
const MAX_VISIBLE_OPTIONS = 100

interface SearchableSelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Label for the always-present empty option. The originals used "All". */
  emptyLabel?: string | null
  id?: string
  disabled?: boolean
  className?: string
  /**
   * The option list is still being fetched.
   *
   * Without this the control looks ready but opens onto "No results found",
   * because `options` is an empty array until the request lands — the same
   * blank list a genuinely empty lookup would give. Showing the wait removes
   * that ambiguity.
   */
  loading?: boolean
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  emptyLabel = 'All',
  id,
  disabled = false,
  className = '',
  loading = false,
}: SearchableSelectProps): JSX.Element {
  const generatedId = useId()
  const controlId = id ?? generatedId
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const allOptions = useMemo<SelectOption[]>(
    () => (emptyLabel === null ? options : [{ value: '', label: emptyLabel }, ...options]),
    [options, emptyLabel],
  )

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return allOptions
    return allOptions.filter((option) => option.label.toLowerCase().includes(needle))
  }, [allOptions, query])

  /**
   * The list is capped because these lookups are big: `city/list` returns 6,899
   * rows, `province/list` 1,143 and `university/list` 736. Rendering one
   * `<button>` per row on open builds thousands of DOM nodes in a single
   * synchronous pass, which is the pause between the click and the menu
   * appearing.
   *
   * Nothing becomes unreachable: `filtered` still searches the WHOLE list, so
   * typing finds any row — only how many are painted at once is limited, and
   * the footer says so whenever rows are held back.
   */
  const visible = useMemo(() => filtered.slice(0, MAX_VISIBLE_OPTIONS), [filtered])
  const hiddenCount = filtered.length - visible.length

  const selected = allOptions.find((option) => option.value === value)

  // Close on an outside click or Escape, the way select2 did.
  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) searchRef.current?.focus()
    else setQuery('')
  }, [isOpen])

  return (
    <div ref={containerRef} className={`position-relative ${className}`}>
      <button
        type="button"
        id={controlId}
        className="form-select text-start d-flex align-items-center justify-content-between"
        // Left enabled rather than `disabled` while loading: Bootstrap paints a
        // disabled control in the same flat grey it uses for dead fields, which
        // reads as broken. The spinner says "working" instead, and the click is
        // swallowed so the empty list can never be opened.
        onClick={() => {
          if (loading) return
          setIsOpen((open) => !open)
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-busy={loading}
      >
        {loading ? (
          <span className="d-flex align-items-center text-muted">
            <span
              className="spinner-border spinner-border-sm text-primary me-2"
              role="status"
              aria-hidden="true"
            />
            Loading…
          </span>
        ) : (
          /*
            select2 shows the PLACEHOLDER whenever the selected option's value is
            empty — it does not show that option's own text. So the "All" entry
            stays selectable in the list below, but the closed control reads
            "Choose Company" rather than "All", exactly as the original did.
          */
          <span className={selected && selected.value !== '' ? '' : 'text-muted'}>
            {selected && selected.value !== '' ? selected.label : placeholder}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show w-100 mt-1 p-0 shadow"
          style={{ maxHeight: 320, overflowY: 'auto', zIndex: 1056 }}
          role="listbox"
        >
          <div className="p-2 border-bottom position-sticky top-0 bg-white">
            <input
              ref={searchRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-muted small">No results found</div>
          ) : (
            <>
              {visible.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`dropdown-item text-wrap ${option.value === value ? 'active' : ''}`}
                  onClick={() => {
                    onChange(option.value)
                    setIsOpen(false)
                  }}
                >
                  {option.label}
                </button>
              ))}

              {hiddenCount > 0 && (
                <div className="px-3 py-2 text-muted small border-top">
                  Showing {visible.length} of {filtered.length.toLocaleString()} — type to narrow
                  the list.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
